import { autocomplete } from "@algolia/autocomplete-js";
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    mapboxAccessToken: String,
  };

  initialize() {
    this.autoComplete = undefined;

    // Create a promise-based debounced version of the geocoding API call
    this.debouncedGeocode = this.createPromiseDebounce(this.geocodeQuery.bind(this), 200);
  }

  // Create a promise-based debounce wrapper
  createPromiseDebounce(func, delay) {
    let timeoutId;
    let lastPromise;

    return (...args) => {
      // Clear any existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Create a new promise that will resolve after the delay
      lastPromise = new Promise((resolve) => {
        timeoutId = setTimeout(() => {
          resolve(func(...args));
        }, delay);
      });

      return lastPromise;
    };
  }

  connect() {
    // Clean up any existing autocomplete instances first
    this.cleanup();

    // Initialize new autocomplete instance
    this.autoComplete = autocomplete({
      autoFocus: false,
      // autoSelect: true,
      container: "#home_autocomplete",
      debug: false,
      detachedMediaQuery: "none",
      enterKeyHint: "search",
      stallThreshold: 500,
      getSources: ({ query, setQuery, refresh }) => {
        return [
          {
            sourceId: "addresses",
            getItems: () => {
              return this.debouncedGeocode(query);
            },
            getItemInputValue: ({ item }) => {
              return item.properties.name;
            },
            getItemUrl: ({ item }) => {
              let components = [];

              // console.log("item props", item.properties);

              if (item.properties.full_address) {
                components.push(`by_address=${item.properties.full_address}`);
              }

              if (item.properties.coordinates) {
                components.push(`by_location[lat]=${item.properties.coordinates.latitude}`);
                components.push(`by_location[lng]=${item.properties.coordinates.longitude}`);
              } else if (item.properties.bbox) {
                components.push(
                  `query=${query}&within_bounds=${item.properties.bbox[1]},${item.properties.bbox[0]},${item.properties.bbox[3]},${item.properties.bbox[2]}`,
                );
              }

              // console.log("item.properties.feature_type", item.properties);

              // switch (item.properties.feature_type) {
              //   case "region":
              //     components.push(`zoom=14`);
              //     break;
              //   case "district":
              //     components.push(`zoom=10`);
              //     break;
              //   case "place":
              //     components.push(`zoom=12`);
              //     break;
              //   case "address":
              //     components.push(`zoom=12`);
              //     break;
              //   default:
              //     components.push(`zoom=10`);
              //     break;
              // }

              return encodeURI(`/search?${components.join("&")}`);
            },
            onActive: (event) => {
              if (
                event.event.target.getBoundingClientRect().bottom > window.innerHeight ||
                event.event.target.getBoundingClientRect().top < 0
              ) {
                event.event.target.scrollIntoView();
              }
            },
            onSelect: (event) => {
              setQuery(event.item.place_formatted);
              Turbo.visit(event.itemUrl);
            },
            templates: {
              header({ html }) {
                return html``;
              },
              item({ item, html }) {
                return html`
                  <div className="flex flex-col gap-1.5">
                    <div class="flex">
                      <div className="flex flex-shrink-0">
                        <div className="flex items-center gap-1.5">
                          <div>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              class="size-5"
                            >
                              <path
                                d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"
                              ></path>
                              <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                          </div>
                          <div class="font-medium">${item.properties.name}</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div class="ml-7 text-sm font-medium opacity-70">${item.properties.place_formatted}</div>
                    </div>
                  </div>
                `;
              },
              noResults() {
                return "No results.";
              },
            },
          },
        ];
      },
      openOnFocus: false,
      placeholder: "Search by address or location",
      plugins: [],
    });
  }

  geocodeQuery(query) {
    return fetch(
      `https://api.mapbox.com/search/geocode/v6/forward?q=${query}&autocomplete=true&format=geojson&limit=5&proximity=ip&country=US&access_token=${this.mapboxAccessTokenValue}`,
    )
      .then((response) => {
        if (!response.ok) {
          console.error("There was an issue geocoding the request.", response.statusText);
          return { features: [] };
        }
        return response.json();
      })
      .then(({ features }) => {
        return features;
      });
  }

  cleanup() {
    try {
      if (this.autoComplete) {
        this.autoComplete.destroy();
      }

      // Remove any remaining autocomplete elements
      const autocompleteElements = document.querySelectorAll(".aa-Autocomplete");
      autocompleteElements.forEach((element) => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });

      // Clear the container
      const container = document.querySelector("#home_autocomplete");
      if (container) {
        container.innerHTML = "";
      }

      // Reset the instance
      this.autoComplete = undefined;
    } catch (error) {
      console.error("Error cleaning up autocomplete", error);
    }
  }

  disconnect() {
    this.cleanup();
  }
}
